import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, ArrowUpRight, Sparkles } from "lucide-react";
import { MEDITATION } from "../data/site";
import { Footer } from "../components/landing/Footer";

const ease = [0.22, 1, 0.36, 1];

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay: 0.15 + i * 0.1 },
  }),
};

export default function FreeMeditation() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().catch(() => {});
      setPlaying(true);
    }
  };

  const restart = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    setCur(0);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };

  const progress = dur ? (cur / dur) * 100 : 0;

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0a0a0c]" data-testid="free-meditation-page">
      {/* glows */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="orb orb-violet absolute -top-32 left-1/4 h-[34rem] w-[34rem] rounded-full bg-[#7c3aed] blur-[150px] opacity-30" />
        <div className="orb orb-peach absolute top-10 right-0 h-[30rem] w-[30rem] rounded-full bg-[#ffb7b2] blur-[150px] opacity-25" />
        <div className="orb orb-indigo absolute bottom-0 left-0 h-[28rem] w-[28rem] rounded-full bg-[#4f46e5] blur-[160px] opacity-20" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-6 py-24">
        <motion.div variants={fade} initial="hidden" animate="show" className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-white/70">
            <Sparkles className="h-3.5 w-3.5 text-[#ffb7b2]" /> Your free meditation
          </span>
        </motion.div>

        <motion.h1
          variants={fade}
          custom={1}
          initial="hidden"
          animate="show"
          className="mt-8 text-center font-display text-4xl font-thin leading-[1.08] tracking-tight text-white md:text-6xl"
        >
          {MEDITATION.title}
        </motion.h1>

        <motion.p
          variants={fade}
          custom={2}
          initial="hidden"
          animate="show"
          className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-white/45"
        >
          {MEDITATION.meta}
        </motion.p>

        {/* player card */}
        <motion.div
          variants={fade}
          custom={3}
          initial="hidden"
          animate="show"
          className="relative mt-12 overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-2xl md:p-12"
          data-testid="free-meditation-player"
        >
          <div className="mx-auto w-full max-w-[280px] overflow-hidden rounded-[1.6rem] border border-white/12 shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)]">
            <img
              src={MEDITATION.cover}
              alt={`${MEDITATION.title} cover art`}
              className="block h-auto w-full"
            />
          </div>

          <div className="mt-9 flex items-center gap-5">
            <button
              onClick={toggle}
              className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]"
              data-testid="free-meditation-play-button"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <Pause className="h-6 w-6 fill-current" />
              ) : (
                <Play className="h-6 w-6 translate-x-[2px] fill-current" />
              )}
            </button>

            <div className="flex-1">
              <div
                onClick={seek}
                className="group relative h-2 cursor-pointer rounded-full bg-white/12"
                data-testid="free-meditation-progress-bar"
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#ffb7b2] to-[#f9d3b4]"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow transition-opacity group-hover:opacity-100"
                  style={{ left: `${progress}%` }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs tabular-nums text-white/50">
                <span data-testid="free-meditation-current-time">{fmt(cur)}</span>
                <span data-testid="free-meditation-duration">{fmt(dur)}</span>
              </div>
            </div>

            <button
              onClick={restart}
              className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              data-testid="free-meditation-restart-button"
              aria-label="Restart"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-7 text-center text-sm leading-relaxed text-white/55">
            Press play, close your eyes, and let this session tune you into a
            luckier, more magnetic state.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          variants={fade}
          custom={4}
          initial="hidden"
          animate="show"
          className="mt-12 flex flex-col items-center"
        >
          <a
            href="/offer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-white px-9 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.35)]"
            data-testid="free-meditation-cta-button"
          >
            Try the full app — one month free
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-5 text-center text-sm text-white/70" data-testid="free-meditation-code-note">
            Use code{" "}
            <span className="rounded-md border border-white/20 bg-white/10 px-2 py-0.5 font-display font-semibold tracking-[0.15em] text-white">
              GRATEFUL
            </span>{" "}
            at checkout.
          </p>
          <p className="mt-3 text-center text-xs text-white/40" data-testid="free-meditation-email-note">
            (please sign up with an email you currently have access to)
          </p>
        </motion.div>
      </div>

      <audio
        ref={audioRef}
        src={MEDITATION.audio}
        preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
      />

      <Footer />
    </div>
  );
}
